import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Settings, Eye, BarChart3 } from 'lucide-react';

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="container-professional py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/welcome"
              className="inline-flex items-center space-x-2 text-gray-600 hover:text-ios-blue mb-6 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Terug naar home</span>
            </Link>
            
            <div className="bg-gradient-to-r from-ios-blue to-blue-600 rounded-2xl shadow-lg p-8 text-white">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Cookie className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold mb-2">Cookiebeleid</h1>
                  <p className="text-blue-100">Laatst bijgewerkt: {new Date().toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8 md:p-12 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Cookie className="w-6 h-6 mr-3 text-ios-blue" />
                1. Wat zijn cookies?
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cookies zijn kleine tekstbestanden die op uw apparaat (computer, tablet of mobiele telefoon) worden geplaatst wanneer u 
                een website bezoekt. Cookies helpen websites om te onthouden wie u bent en hoe u de site gebruikt, waardoor uw ervaring 
                wordt verbeterd.
              </p>
              <p className="text-gray-700 leading-relaxed">
                StudyStay gebruikt cookies en vergelijkbare technologieën om onze Dienst te leveren, te verbeteren en te personaliseren.
              </p>
            </section>

            {/* Types of Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Soorten cookies die wij gebruiken</h2>
              <div className="space-y-6">
                <div className="bg-blue-50 border-l-4 border-ios-blue p-5 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <Settings className="w-6 h-6 text-ios-blue" />
                    <h3 className="text-xl font-bold text-gray-900">2.1 Essentiële cookies</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Deze cookies zijn noodzakelijk voor het functioneren van onze website. Zij maken het mogelijk om ingelogd te blijven 
                    en basis functionaliteiten te gebruiken.
                  </p>
                  <div className="bg-white p-4 rounded">
                    <p className="text-sm text-gray-600 mb-2"><strong>Voorbeelden:</strong></p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                      <li>Sessiebeheer en authenticatie</li>
                      <li>Winkelwagen en voorkeuren</li>
                      <li>Beveiligingsfuncties</li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-3"><strong>Bewaartermijn:</strong> Sessie of maximaal 1 jaar</p>
                  </div>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-5 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <Eye className="w-6 h-6 text-green-600" />
                    <h3 className="text-xl font-bold text-gray-900">2.2 Functionele cookies</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Deze cookies onthouden uw voorkeuren en keuzes om uw ervaring te personaliseren.
                  </p>
                  <div className="bg-white p-4 rounded">
                    <p className="text-sm text-gray-600 mb-2"><strong>Voorbeelden:</strong></p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                      <li>Taalvoorkeuren</li>
                      <li>Regio-instellingen</li>
                      <li>Zoekgeschiedenis</li>
                      <li>Favoriete filters</li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-3"><strong>Bewaartermijn:</strong> Maximaal 1 jaar</p>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-5 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <BarChart3 className="w-6 h-6 text-yellow-600" />
                    <h3 className="text-xl font-bold text-gray-900">2.3 Analytische cookies</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Deze cookies helpen ons begrijpen hoe bezoekers onze website gebruiken, zodat wij de Dienst kunnen verbeteren.
                  </p>
                  <div className="bg-white p-4 rounded">
                    <p className="text-sm text-gray-600 mb-2"><strong>Voorbeelden:</strong></p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                      <li>Aantal bezoekers en paginaweergaven</li>
                      <li>Populaire pagina's en functies</li>
                      <li>Foutmeldingen en technische problemen</li>
                      <li>Gebruikersgedrag en navigatiepatronen</li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-3"><strong>Bewaartermijn:</strong> Maximaal 2 jaar</p>
                    <p className="text-sm text-gray-500 mt-2 italic">
                      Deze cookies worden alleen gebruikt voor statistische doeleinden en bevatten geen persoonlijk identificeerbare informatie.
                    </p>
                  </div>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-500 p-5 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <Cookie className="w-6 h-6 text-purple-600" />
                    <h3 className="text-xl font-bold text-gray-900">2.4 Marketing cookies</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Deze cookies worden gebruikt om relevante advertenties te tonen en de effectiviteit van marketingcampagnes te meten.
                  </p>
                  <div className="bg-white p-4 rounded">
                    <p className="text-sm text-gray-600 mb-2"><strong>Voorbeelden:</strong></p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                      <li>Gepersonaliseerde advertenties</li>
                      <li>Conversietracking</li>
                      <li>Remarketing</li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-3"><strong>Bewaartermijn:</strong> Maximaal 1 jaar</p>
                    <p className="text-sm text-gray-500 mt-2 italic">
                      <strong>Let op:</strong> Wij gebruiken momenteel geen marketing cookies, maar behouden ons het recht voor deze in de toekomst te gebruiken.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Third Party Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Cookies van derden</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Sommige cookies op ons platform worden geplaatst door derde partijen. Deze partijen hebben hun eigen privacybeleid 
                en cookiebeleid.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2"><strong>Voorbeelden van derde partijen:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                  <li><strong>Google Analytics:</strong> Voor website-analyse en statistieken</li>
                  <li><strong>Firebase:</strong> Voor authenticatie en database-opslag</li>
                  <li><strong>Betalingsverwerkers:</strong> Voor veilige betalingstransacties</li>
                </ul>
              </div>
            </section>

            {/* Cookie Management */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Cookiebeheer</h2>
              <div className="space-y-4">
                <div className="bg-blue-50 border-l-4 border-ios-blue p-4 rounded">
                  <h3 className="font-semibold text-gray-900 mb-2">4.1 Browserinstellingen</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    De meeste browsers staan u toe om cookies te beheren. U kunt cookies accepteren, weigeren of verwijderen via uw 
                    browserinstellingen.
                  </p>
                  <p className="text-sm text-gray-600 italic">
                    <strong>Belangrijk:</strong> Het weigeren van essentiële cookies kan de functionaliteit van onze website beperken.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">4.2 Cookie-instellingen op StudyStay</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Wij werken aan een cookie-instellingencentrum waar u uw voorkeuren kunt beheren. Momenteel kunt u cookies beheren 
                    via uw browserinstellingen.
                  </p>
                </div>
              </div>
            </section>

            {/* Browser Instructions */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Hoe cookies te beheren per browser</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Google Chrome</h3>
                  <p className="text-sm text-gray-700">
                    Instellingen → Privacy en beveiliging → Cookies en andere sitegegevens
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Mozilla Firefox</h3>
                  <p className="text-sm text-gray-700">
                    Opties → Privacy & Beveiliging → Cookies en sitegegevens
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Safari</h3>
                  <p className="text-sm text-gray-700">
                    Voorkeuren → Privacy → Cookies en websitegegevens
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Microsoft Edge</h3>
                  <p className="text-sm text-gray-700">
                    Instellingen → Cookies en site machtigingen → Cookies en sitegegevens
                  </p>
                </div>
              </div>
            </section>

            {/* Do Not Track */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. "Do Not Track" signalen</h2>
              <p className="text-gray-700 leading-relaxed">
                Ons platform respecteert momenteel geen "Do Not Track" (DNT) signalen die door uw browser worden verzonden. 
                Wij evalueren dit regelmatig en kunnen in de toekomst DNT-signalen gaan respecteren.
              </p>
            </section>

            {/* Updates */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Wijzigingen in dit Cookiebeleid</h2>
              <p className="text-gray-700 leading-relaxed">
                Wij kunnen dit Cookiebeleid van tijd tot tijd bijwerken om wijzigingen in onze cookies of om andere operationele, 
                juridische of regelgevende redenen weer te geven. De datum van de laatste wijziging staat bovenaan dit document.
              </p>
            </section>

            {/* More Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Meer informatie</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Voor meer informatie over hoe wij uw gegevens verzamelen en gebruiken, zie ons 
                <Link to="/privacy" className="text-ios-blue hover:underline font-medium"> Privacybeleid</Link>.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Voor informatie over onze algemene voorwaarden, zie onze 
                <Link to="/terms" className="text-ios-blue hover:underline font-medium"> Algemene Voorwaarden</Link>.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-gradient-to-r from-ios-blue to-blue-600 rounded-xl p-6 text-white">
              <h2 className="text-2xl font-bold mb-4">9. Contact</h2>
              <p className="text-blue-100 leading-relaxed mb-4">
                Als u vragen heeft over ons gebruik van cookies, neem dan contact met ons op:
              </p>
              <div className="space-y-2 text-blue-100">
                <p><strong>Official email:</strong> info@studystay.org</p>
                <p><strong>Registry contact email:</strong> support@enty.io</p>
                <p><strong>Address:</strong> Tornimäe tn 5, 10145 Tallinn, Estonia</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

